// 多鏡頭攝影機（plan SPEC #multi-lens；第一台：喬安 W6-U）在 VMS 的共用部分：
//   - 鏡頭宣告：shadow reported 的 kinio_lens（web-sdk parseLensDecl；不合規 / 沒有 = null = 單鏡頭，畫面與行為完全不變）
//   - 鏡頭名稱：依 lenses[].position（上鏡頭 / 下鏡頭 / 廣角…，other =「鏡頭 n」）；不知道宣告時一律「鏡頭 n」
// 只對生效能力有 kinio_lens 的設備讀 shadow（模組快取 60 秒；不靠 QueryClient，影像牆每一格都能用）。
import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { getShadow, parseLensDecl, type LensDecl } from '@kinio/web-sdk';
import type { VmsDevice } from './devices';

/** 這台宣告了多鏡頭（生效能力有 kinio_lens；通道沒有）。 */
export function maybeMultiLens(cam: Pick<VmsDevice, 'capabilities' | 'reportedClusters' | 'parentAid'> | null | undefined): boolean {
  if (!cam || cam.parentAid) return false;
  return !!cam.capabilities?.includes('kinio_lens') || !!cam.reportedClusters?.includes('kinio_lens');
}

const TTL_MS = 60_000;
const cache = new Map<string, { at: number; p: Promise<LensDecl | null> }>();

/** 讀 shadow 的 kinio_lens（同一台 60 秒內共用一次請求；讀不到 = null = 單鏡頭）。 */
export function loadLensDecl(api: string, homeId: string, aid: string, now = Date.now()): Promise<LensDecl | null> {
  const key = `${api}|${homeId}/${aid}`;
  const hit = cache.get(key);
  if (hit && now - hit.at < TTL_MS) return hit.p;
  const p = getShadow(api, homeId, aid)
    .then((s) => parseLensDecl((s?.state?.reported as Record<string, unknown> | undefined)?.kinio_lens))
    .catch(() => {
      cache.delete(key);
      return null;
    });
  cache.set(key, { at: now, p });
  return p;
}

/** 測試用。 */
export function resetLensDecls(): void {
  cache.clear();
}

type LensCam = Pick<VmsDevice, 'apiEndpoint' | 'homeId' | 'aid' | 'capabilities' | 'reportedClusters' | 'parentAid'>;

/** 這台的鏡頭宣告；單鏡頭 / 還沒讀到 = null。 */
export function useLensDecl(cam: LensCam | null | undefined): LensDecl | null {
  return useLensDeclState(cam).decl;
}

/**
 * 同 useLensDecl，另外說讀完了沒（ready）：直播格要等它才開連線——不然宣告晚到會整條重開（W6-U 人的名額只有 1，
 * 前一條還沒收掉就被 MAX_SESSIONS_REACHED 擋）。不是多鏡頭機種一律 ready。
 */
export function useLensDeclState(cam: LensCam | null | undefined): { decl: LensDecl | null; ready: boolean } {
  const on = maybeMultiLens(cam);
  const key = on && cam ? `${cam.apiEndpoint}|${cam.homeId}/${cam.aid}` : '';
  const [st, setSt] = useState<{ key: string; decl: LensDecl | null }>({ key: '', decl: null });
  useEffect(() => {
    if (!key || !cam) return;
    let alive = true;
    void loadLensDecl(cam.apiEndpoint, cam.homeId, cam.aid).then((decl) => alive && setSt({ key, decl }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key 已含 api / home / aid
  }, [key]);
  return { decl: on && st.key === key ? st.decl : null, ready: !on || st.key === key };
}

/** 鏡頭名稱：有宣告照 position；沒有（或宣告裡沒有這顆）=「鏡頭 n」（n = id + 1）。 */
export function lensName(t: TFunction, decl: LensDecl | null | undefined, id: number): string {
  const l = decl?.lenses.find((x) => x.id === id);
  return t(`lens.${l ? l.nameKey : 'lens_n'}`, { n: id + 1 });
}
